import { FC, FormEvent, useEffect, useRef, useState } from 'react'
import { toDataURL } from 'qrcode'
import { confirmTotp, disableSecondFactor, reissueRecoveryCodes, startTotp } from '../core/_requests'
import { accountErrorText, dataOf } from '../core/errors'
import { AccountOverview, AskForPassword, TOTP_CODE_LENGTH, TotpEnrolment } from '../core/_models'
import { RecoveryCodes } from './RecoveryCodes'

type Props = {
  overview: AccountOverview
  ask: AskForPassword
  onChange: (overview: AccountOverview) => void
}

const DIGITS_ONLY = /^\d*$/

/** The authenticator app is the second factor itself; recovery codes are its way back in. */
export const SecondFactorCard: FC<Props> = ({ overview, ask, onChange }) => {
  const [enrolment, setEnrolment] = useState<TotpEnrolment | null>(null)
  const [qr, setQr] = useState<string | null>(null)
  const [code, setCode] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [freshCodes, setFreshCodes] = useState<string[] | null>(null)
  const codeInput = useRef<HTMLInputElement>(null)

  // after a wrong code the field comes back enabled: put the cursor back for the retry
  useEffect(() => {
    if (!loading && error) {
      codeInput.current?.focus()
    }
  }, [loading, error])

  useEffect(() => {
    let alive = true
    setQr(null)
    if (enrolment) {
      // drawn here, in the browser: the seed is never sent to anybody to be turned into a picture
      toDataURL(enrolment.uri)
        .then((url) => alive && setQr(url))
        .catch(() => alive && setQr(null))
    }

    return () => {
      alive = false
    }
  }, [enrolment])

  const start = () =>
    ask({
      title: 'Подключение приложения-аутентификатора',
      action: async (password) => {
        setEnrolment(dataOf(await startTotp(password)))
        setCode('')
        setError(null)
      },
    })

  const confirm = async (event: FormEvent) => {
    event.preventDefault()
    if (loading || code.length !== TOTP_CODE_LENGTH) {
      return
    }

    setLoading(true)
    setError(null)
    try {
      const { recoveryCodes } = dataOf(await confirmTotp(code))
      setEnrolment(null)
      setFreshCodes(recoveryCodes)
      onChange({ ...overview, secondFactorEnabled: true, hasRecoveryCodes: true })
    } catch (e) {
      setCode('')
      setError(accountErrorText(e))
    } finally {
      setLoading(false)
    }
  }

  const reissue = () =>
    ask({
      title: 'Новые резервные коды',
      warning: 'Прежние резервные коды перестанут работать.',
      action: async (password) => {
        setFreshCodes(dataOf(await reissueRecoveryCodes(password)).recoveryCodes)
      },
    })

  const disable = () =>
    ask({
      title: 'Выключение второго фактора',
      warning:
        'Вход снова будет только по паролю. Приложение-аутентификатор, все ключи и резервные коды будут удалены.',
      action: async (password) => {
        const next = dataOf(await disableSecondFactor(password))
        setFreshCodes(null)
        onChange(next)
      },
    })

  return (
    <div className='card mb-7'>
      <div className='card-header'>
        <h3 className='card-title fw-bold'>Второй фактор</h3>
        <div className='card-toolbar'>
          {overview.secondFactorEnabled ? (
            <span className='badge badge-light-success fs-7'>Второй фактор включён</span>
          ) : (
            <span className='badge badge-light-warning fs-7'>Второй фактор не включён</span>
          )}
        </div>
      </div>
      <div className='card-body'>
        <h4 className='fw-bold mb-3'>Приложение-аутентификатор (TOTP)</h4>

        {freshCodes && <RecoveryCodes codes={freshCodes} onDismiss={() => setFreshCodes(null)} />}

        {!overview.secondFactorEnabled && !enrolment && (
          <>
            <p className='text-gray-700 mb-7'>
              После пароля вход подтверждается одноразовым кодом из Google Authenticator или
              совместимого приложения. Начнёт действовать со следующего входа.
            </p>
            <button type='button' className='btn btn-primary' onClick={start}>
              Подключить приложение
            </button>
          </>
        )}

        {enrolment && (
          <form onSubmit={confirm} noValidate>
            <p className='text-gray-700'>
              Отсканируйте код приложением или введите ключ вручную, затем введите код из приложения.
            </p>
            <div className='d-flex flex-wrap align-items-center gap-6 mb-5'>
              {qr ? (
                <img src={qr} alt='QR-код для приложения-аутентификатора' width={180} height={180} className='border rounded bg-white p-2' />
              ) : (
                <div className='text-muted'>QR-код недоступен — введите ключ вручную.</div>
              )}
              <div>
                <div className='text-muted fs-7 mb-1'>Ключ</div>
                <code className='fs-5 user-select-all'>{enrolment.secret}</code>
              </div>
            </div>

            <label className='form-label fw-bold' htmlFor='account_totp_code'>
              Код из приложения
            </label>
            <input
              id='account_totp_code'
              ref={codeInput}
              type='text'
              inputMode='numeric'
              autoComplete='one-time-code'
              className='form-control mb-4'
              style={{ maxWidth: 200 }}
              value={code}
              disabled={loading}
              onChange={(event) => {
                const value = event.target.value
                if (DIGITS_ONLY.test(value) && value.length <= TOTP_CODE_LENGTH) {
                  setCode(value)
                }
              }}
            />
            {error && <div className='text-danger mb-4'>{error}</div>}

            <div className='d-flex gap-3'>
              <button type='submit' className='btn btn-primary' disabled={loading || code.length !== TOTP_CODE_LENGTH}>
                {loading ? (
                  <>
                    Проверяем... <span className='spinner-border spinner-border-sm align-middle ms-2' />
                  </>
                ) : (
                  'Подтвердить'
                )}
              </button>
              <button type='button' className='btn btn-light' disabled={loading} onClick={() => setEnrolment(null)}>
                Отмена
              </button>
            </div>
          </form>
        )}

        {overview.secondFactorEnabled && (
          <div className='d-flex flex-wrap gap-3'>
            <button type='button' className='btn btn-light-primary' onClick={reissue}>
              Выпустить новые коды
            </button>
            <button type='button' className='btn btn-light-danger' onClick={disable}>
              Выключить второй фактор
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
